import { useEffect, useMemo } from 'react';
import type { MUOBankResponse } from '@gcv/shared';
import { DueDiligenceStatus } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { BanksApi } from 'api';
import { MUOS_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';

export function useMuos(selectFn?: (data: MUOBankResponse[]) => MUOBankResponse[] | []) {
  const bankApi = new BanksApi();
  const bankDispensaryStore = getFiDispensaryStore();
  const { userBankId } = useUser();

  const { data: allMuos, isLoading: isLoadingMuos } = useQuery({
    enabled: !!userBankId.length,
    queryKey: [MUOS_KEY, userBankId],
    queryFn: () => bankApi.getMuo(userBankId),
    select: selectFn
  });

  const { activeMuos, archivedMuos } = useMemo(() => {
    if (!allMuos) return { activeMuos: undefined, archivedMuos: undefined };

    const activeMuos = allMuos.filter(
      ({ assigned_onboarding_template }) =>
        assigned_onboarding_template.status !== DueDiligenceStatus.BANK_DISCONNECTED
    );
    const archivedMuos = allMuos.filter(
      ({ assigned_onboarding_template }) =>
        assigned_onboarding_template.status === DueDiligenceStatus.BANK_DISCONNECTED
    );

    return { activeMuos, archivedMuos };
  }, [allMuos]);

  useEffect(() => {
    if (activeMuos?.length) {
      bankDispensaryStore.setBankMuos(activeMuos);
    }
    if (archivedMuos?.length) {
      bankDispensaryStore.setBankMuosArchived(archivedMuos);
    }
  }, [activeMuos, archivedMuos]);

  return { allMuos, activeMuos, archivedMuos, isLoadingMuos };
}

export const useMuo = (id: string) => {
  const { allMuos } = useMuos((data: MUOBankResponse[]) => {
    const muo = data.find((muo: MUOBankResponse) => muo.id === id);
    if (muo) return [muo];
    return [];
  });
  if (!allMuos || allMuos.length === 0) {
    throw new Error(`No parent dispensary found with id: ${id}`);
  }
  return allMuos[0];
};
